import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import VaccineProductionChart from "./VaccineProductionChart";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineProductionReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    VaccineID: "",
    LotID: "",
    BudgetType: "",
    StartDate: "",
    EndDate: "",
  });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [livestockTypeOptions, setLivestockTypeOptions] = useState([]);
  const [productionData, setProductionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // ดึงข้อมูล Livestock Type List
  const fetchLivestockTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const types = response.LivestockTypeList || [];
        setLivestockTypeOptions(types);
        if (types.length === 0) {
          console.warn("LivestockTypeList is empty");
        }
      } else {
        console.error("Failed to fetch livestock types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  // เรียก API getVaccineProductionSummary
  const fetchProductionReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineProductionSummary",
        data: {
          VaccineID: filterData.VaccineID || "",
          LivestockTypeID: "",
          LotID: filterData.LotID || "",
          BudgetType: filterData.BudgetType || "",
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setProductionData(response.ProductionReport || []);
      } else {
        setProductionData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching production report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccineOptions();
      fetchLivestockTypeOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทงบประมาณ</span>
        </HStack>
      ),
      selector: (row) => (row.BudgetType === "Prevention" ? "งบป้องกัน" : "งบจำหน่าย"),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (EN)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameEN,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทปศุสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.LivestockType,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนผลิต</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalQuantity)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่ผลิต</span>
        </HStack>
      ),
      selector: (row) => new Date(row.ProductionDate).toLocaleDateString("th-TH"),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่หมดอายุ</span>
        </HStack>
      ),
      selector: (row) => new Date(row.ExpiryDate).toLocaleDateString("th-TH"),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>อายุคงเหลือ (วัน)</span>
        </HStack>
      ),
      selector: (row) => row.ExpiryDays,
      sortable: true,
      width: "200px",
    },
  ];

  const exportToExcel = () => {
    const wsData = productionData.map((item) => ({
      "ประเภทงบประมาณ": item.BudgetType === "Prevention" ? "งบป้องกัน" : "งบจำหน่าย",
      "ชื่อวัคซีน (TH)": item.VaccineNameTH,
      "ชื่อวัคซีน (EN)": item.VaccineNameEN,
      "ประเภทปศุสัตว์": item.LivestockType,
      "Lot No.": item.LotID,
      "จำนวนผลิต": item.TotalQuantity,
      "วันที่ผลิต": new Date(item.ProductionDate).toLocaleDateString("th-TH"),
      "วันที่หมดอายุ": new Date(item.ExpiryDate).toLocaleDateString("th-TH"),
      "อายุคงเหลือ (วัน)": item.ExpiryDays,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ProductionReport");
    XLSX.writeFile(wb, "VaccineProductionReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานการผลิตวัคซีน", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "ประเภทงบประมาณ",
          "ชื่อวัคซีน (TH)",
          "ชื่อวัคซีน (EN)",
          "ประเภทปศุสัตว์",
          "Lot No.",
          "จำนวนผลิต",
          "วันที่ผลิต",
          "วันที่หมดอายุ",
          "อายุคงเหลือ (วัน)",
        ],
      ],
      body: productionData.map((item) => [
        item.BudgetType === "Prevention" ? "งบป้องกัน" : "งบจำหน่าย",
        item.VaccineNameTH,
        item.VaccineNameEN,
        item.LivestockType,
        item.LotID,
        item.TotalQuantity,
        new Date(item.ProductionDate).toLocaleDateString("th-TH"),
        new Date(item.ExpiryDate).toLocaleDateString("th-TH"),
        item.ExpiryDays,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 12,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 60 },
        2: { cellWidth: 40 },
        3: { cellWidth: 40 },
        4: { cellWidth: 30 },
        7: { cellWidth: 40 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineProductionReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Production Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Production Report</h2>
          <table>
            <thead>
              <tr>
                <th>ประเภทงบประมาณ</th>
                <th>ชื่อวัคซีน (TH)</th>
                <th>ชื่อวัคซีน (EN)</th>
                <th>ประเภทปศุสัตว์</th>
                <th>Lot No.</th>
                <th>จำนวนผลิต</th>
                <th>วันที่ผลิต</th>
                <th>วันที่หมดอายุ</th>
                <th>อายุคงเหลือ (วัน)</th>
              </tr>
            </thead>
            <tbody>
              ${productionData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.BudgetType === "Prevention" ? "งบป้องกัน" : "งบจำหน่าย"}</td>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.VaccineNameEN}</td>
                      <td>${item.LivestockType}</td>
                      <td>${item.LotID}</td>
                      <td>${Number(item.TotalQuantity)?.toLocaleString()}</td>
                      <td>${new Date(item.ProductionDate).toLocaleDateString("th-TH")}</td>
                      <td>${new Date(item.ExpiryDate).toLocaleDateString("th-TH")}</td>
                      <td>${item.ExpiryDays}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        รายงานการผลิตวัคซีน
      </Heading>

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faSearch} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              ค้นหาข้อมูลการผลิต
            </Heading>
          </HStack>
        </Box>
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทงบประมาณ
              </FormLabel>
              <Select
                name="BudgetType"
                value={filterData.BudgetType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทงบประมาณ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                <option value="Prevention">งบป้องกัน</option>
                <option value="Distribution">งบจำหน่าย</option>
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                Lot No.
              </FormLabel>
              <Input
                name="LotID"
                value={filterData.LotID}
                onChange={handleInputChange}
                placeholder="กรอก Lot No. (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่ผลิต (ตั้งแต่)
              </FormLabel>
              <Input
                type="date"
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่ผลิต (ถึง)
              </FormLabel>
              <Input
                type="date"
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    BudgetType: "",
                    VaccineID: "",
                    LotID: "",
                    StartDate: "",
                    EndDate: "",
                  })
                }
              />
              <Search onClick={fetchProductionReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {productionData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box
            mb={5}
            borderRadius="full"
            w="100%"
            bgColor="iOrange.500"
            color="white"
            p={2}
          >
            <HStack spacing={5} ml={5}>
              <FontAwesomeIcon icon={faTable} color="white" size="xl" />
              <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                รายงานการผลิตวัคซีน
              </Heading>
            </HStack>
          </Box>
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={productionData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {productionData.length > 0 && (
        <VaccineProductionChart
          data={productionData}
          livestockTypeOptions={livestockTypeOptions}
        />
      )}
    </Box>
  );
};

export default VaccineProductionReport;